import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Button,
  TextInput,
  Table,
  Badge,
  Loader,
  Center,
  Select,
  Progress,
  ActionIcon,
  SimpleGrid,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { Plus, Search, Users, Grid, List, Settings, Download } from 'react-feather';
import { IconMailOff } from '@tabler/icons-react';
import { cohortsApi } from '../../services/api/cohorts';
import type { CohortWithDetails, CohortStatus, OverallHealthStatus } from '../../types';
import { COHORT_STATUS_CONFIG } from '../../config/statusConfig';
import { OVERALL_HEALTH_CONFIG } from '../../config/overviewConfig';
import { formatDateOnlyRange } from '../../utils/formatters';
import { CreateCohortModal } from './CreateCohortPage';
import { SortableTableHeader, Pagination, ColumnSettingsPopover, ExportModal, EmptyState } from '../../components/common';
import { useColumnConfig, useFetchList, type ColumnConfig } from '../../hooks';
import classes from './CohortsPage.module.css';

// Default column configuration for cohorts table
const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'code', label: 'Code', visible: true, sortable: true },
  { id: 'name', label: 'Cohort Name', visible: true, sortable: true },
  { id: 'partner', label: 'Partner', visible: true, sortable: true },
  { id: 'status', label: 'Status', visible: true, sortable: true },
  { id: 'dates', label: 'Dates', visible: true, sortable: true },
  { id: 'sessions', label: 'Sessions', visible: true, sortable: false },
  { id: 'health', label: 'Health', visible: true, sortable: true },
];

// Status filter pill options
const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'in_prep', label: 'In Prep' },
  { value: 'in_design', label: 'In Design' },
  { value: 'draft', label: 'Draft' },
  { value: 'completed', label: 'Completed' },
];

// Sort options for dropdown
const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'code', label: 'Code' },
  { value: 'partner', label: 'Partner' },
  { value: 'status', label: 'Status' },
  { value: 'sessions', label: 'Sessions Complete' },
  { value: 'health', label: 'Health' },
];

type ViewMode = 'list' | 'grid';

// MED-114/MED-394: Health indicator using real data from API
const HEALTH_DOT_COLORS: Record<OverallHealthStatus, string> = {
  at_risk: '#D64545',
  monitor: '#E9A23B',
  on_track: '#4A7C59',
  no_data: '#ccc',
};

function HealthIndicator({ healthStatus, status }: { healthStatus?: OverallHealthStatus; status: CohortStatus }) {
  // Cancelled/archived cohorts always show as no_data
  const effectiveStatus: OverallHealthStatus =
    (status === 'cancelled' || status === 'archived') ? 'no_data' : (healthStatus ?? 'no_data');

  const config = OVERALL_HEALTH_CONFIG[effectiveStatus];
  const dotColor = HEALTH_DOT_COLORS[effectiveStatus];

  return (
    <Group gap={6} wrap="nowrap">
      <span className={classes.healthDot} style={{ backgroundColor: dotColor }} />
      <Text size="sm" c={effectiveStatus === 'no_data' ? 'dimmed' : dotColor} fw={500}>{config.label}</Text>
    </Group>
  );
}

const SORT_BY_MAP: Record<string, string> = {
  code: 'code',
  name: 'name',
  partner: 'partner_name',
  status: 'status',
  dates: 'start_date',
  sessions: 'session_count',
  health: 'health_status',
};

export function CohortsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] = useDisclosure(false);

  // Use the reusable column config hook
  const {
    columns,
    setColumns,
    sortConfig,
    handleSort,
    clearSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    visibleColumnCount,
  } = useColumnConfig({
    preferenceKey: 'cohorts_list_settings',
    defaultColumns: DEFAULT_COLUMNS,
    defaultPageSize: 25,
  });

  // Modal state - controlled by URL param
  const createModalOpened = searchParams.get('create') === 'true';

  const openCreateModal = () => {
    setSearchParams({ create: 'true' });
  };

  const closeCreateModal = () => {
    setSearchParams({});
  };

  const buildCohortsRequest = useCallback(() => {
    const effectiveSortBy = sortConfig?.key || sortBy;
    const apiSortBy = SORT_BY_MAP[effectiveSortBy] || 'name';
    const defaultOrder = effectiveSortBy === 'sessions' ? 'DESC' : 'ASC';
    const apiSortOrder = sortConfig?.order || defaultOrder;
    const statusFilter = selectedStatusFilter === 'all'
      ? undefined
      : [selectedStatusFilter] as CohortStatus[];

    return cohortsApi.list({
      search: debouncedSearch || undefined,
      status: statusFilter,
      sortBy: apiSortBy,
      sortOrder: apiSortOrder,
      page,
      limit: pageSize,
    });
  }, [debouncedSearch, selectedStatusFilter, sortBy, sortConfig, page, pageSize]);

  const { data: cohorts, loading, totalCount, refetch: fetchCohorts } = useFetchList(buildCohortsRequest);
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleCohortCreated = () => {
    fetchCohorts();
  };

  const handleRowClick = (cohortId: string) => {
    navigate(`/cohorts/${cohortId}`);
  };

  // Render column header based on column config
  const renderColumnHeader = (column: ColumnConfig) => {
    if (column.sortable) {
      return (
        <Table.Th key={column.id} className={classes[`${column.id}Column`]}>
          <SortableTableHeader
            label={column.label.toUpperCase()}
            sortKey={column.id}
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </Table.Th>
      );
    }
    return (
      <Table.Th key={column.id} className={classes[`${column.id}Column`]}>
        {column.label.toUpperCase()}
      </Table.Th>
    );
  };

  // Render cell content based on column id
  const renderCellContent = (column: ColumnConfig, cohort: CohortWithDetails) => {
    switch (column.id) {
      case 'code':
        return (
          <Text fw={600} size="sm" c="dimmed">
            {cohort.code || '—'}
          </Text>
        );
      case 'name':
        return (
          <Group gap="xs" wrap="nowrap">
            <Text fw={500}>{cohort.name}</Text>
            {cohort.disabled_email_recipients?.length > 0 && (
              <Tooltip label={`Emails disabled for: ${cohort.disabled_email_recipients.map((r: string) => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}`}>
                <IconMailOff size={14} color="var(--mantine-color-gray-6)" />
              </Tooltip>
            )}
          </Group>
        );
      case 'partner':
        return (
          <Text size="sm">
            {cohort.partner_code ? `${cohort.partner_code} - ` : ''}
            {cohort.partner_name}
          </Text>
        );
      case 'status':
        return (
          <Badge
            color={COHORT_STATUS_CONFIG[cohort.status].color}
            variant={COHORT_STATUS_CONFIG[cohort.status].variant || 'light'}
            size="sm"
          >
            {COHORT_STATUS_CONFIG[cohort.status].label}
          </Badge>
        );
      case 'dates':
        return (
          <Text size="sm" c="dimmed">
            {formatDateOnlyRange(cohort.start_date, cohort.end_date)}
          </Text>
        );
      case 'sessions': {
        const completed = cohort.completed_sessions ?? 0;
        const total = cohort.session_count ?? 0;
        const progress = total > 0 ? (completed / total) * 100 : 0;
        return (
          <Group gap={8} wrap="nowrap">
            <Text size="sm" fw={500}>
              {completed}/{total}
            </Text>
            <Progress
              value={progress}
              size="sm"
              color="medleyGreen"
              w={60}
              className={classes.sessionProgress}
            />
          </Group>
        );
      }
      case 'health':
        return <HealthIndicator healthStatus={cohort.health_status} status={cohort.status} />;
      default:
        return <Text size="sm">—</Text>;
    }
  };

  // Empty state
  const hasFilters = debouncedSearch || selectedStatusFilter !== 'all';
  if (!loading && cohorts.length === 0 && !hasFilters) {
    return (
      <Container size="xl" py="xl">
        <Title order={1} mb="xl">
          Cohorts
        </Title>
        <EmptyState
          icon={<Users size={40} />}
          title="No cohorts yet"
          description="Get started by creating your first cohort."
          action={
            <Button leftSection={<Plus size={18} />} onClick={openCreateModal}>
              Create Cohort
            </Button>
          }
          size="lg"
          withCard
        />

        {/* Create Cohort Modal */}
        <CreateCohortModal
          opened={createModalOpened}
          onClose={closeCreateModal}
          onCreated={handleCohortCreated}
        />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={1}>Cohorts</Title>
        <Group gap="sm">
          <Button variant="default" leftSection={<Download size={18} />} onClick={openExportModal}>
            Export
          </Button>
          <Button leftSection={<Plus size={18} />} onClick={openCreateModal}>
            Create Cohort
          </Button>
        </Group>
      </Group>

      {/* Search and Filter Panel */}
      <Card withBorder p="md" mb="lg" className={classes.filterPanel} styles={{ root: { boxShadow: 'none' } }}>
        <Group justify="space-between" wrap="nowrap">
          {/* Left: Search and Filter Pills */}
          <Group gap="md" wrap="nowrap">
            <TextInput
              placeholder="Search cohorts..."
              leftSection={<Search size={16} />}
              value={searchValue}
              onChange={e => setSearchValue(e.currentTarget.value)}
              className={classes.searchInput}
            />

            {/* Status Filter Pills */}
            <Group gap={4}>
              {STATUS_FILTER_OPTIONS.map(option => (
                <Button
                  key={option.value}
                  variant={selectedStatusFilter === option.value ? 'filled' : 'outline'}
                  color={selectedStatusFilter === option.value ? 'medleyGreen' : 'gray'}
                  size="xs"
                  className={classes.filterPill}
                  onClick={() => setSelectedStatusFilter(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </Group>
          </Group>

          {/* Right: Sort and View Toggle */}
          <Group gap="md" wrap="nowrap">
            <Select
              placeholder="Sort by"
              data={SORT_OPTIONS}
              value={sortBy}
              onChange={value => { setSortBy(value || 'name'); clearSort(); }}
              leftSection={<Text size="xs" c="dimmed">Sort by:</Text>}
              leftSectionWidth={60}
              w={180}
              size="sm"
              styles={{
                input: { paddingLeft: 60 },
              }}
            />

            {/* View Mode Toggle */}
            <Group gap={0} className={classes.viewToggle}>
              <ActionIcon
                variant={viewMode === 'grid' ? 'filled' : 'subtle'}
                color={viewMode === 'grid' ? 'medleyGreen' : 'gray'}
                size="md"
                onClick={() => setViewMode('grid')}
                className={classes.viewToggleButton}
              >
                <Grid size={16} />
              </ActionIcon>
              <ActionIcon
                variant={viewMode === 'list' ? 'filled' : 'subtle'}
                color={viewMode === 'list' ? 'medleyGreen' : 'gray'}
                size="md"
                onClick={() => setViewMode('list')}
                className={classes.viewToggleButton}
              >
                <List size={16} />
              </ActionIcon>
            </Group>
          </Group>
        </Group>
      </Card>

      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : viewMode === 'list' ? (
        /* Table/List View */
        <Card withBorder p={0} styles={{ root: { boxShadow: 'none' } }}>
          <Table.ScrollContainer minWidth={900}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  {columns
                    .filter(col => col.visible)
                    .map(col => renderColumnHeader(col))}
                  {/* Settings Gear Column */}
                  <Table.Th className={classes.settingsColumn}>
                    <ColumnSettingsPopover
                      columns={columns}
                      onColumnsChange={setColumns}
                      trigger={
                        <ActionIcon variant="subtle" color="gray" size="sm">
                          <Settings size={16} />
                        </ActionIcon>
                      }
                    />
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {cohorts.map(cohort => (
                  <Table.Tr
                    key={cohort.id}
                    className={classes.tableRow}
                    onClick={() => handleRowClick(cohort.id)}
                  >
                    {columns
                      .filter(col => col.visible)
                      .map(col => (
                        <Table.Td key={col.id}>
                          {renderCellContent(col, cohort)}
                        </Table.Td>
                      ))}
                    {/* Empty cell for settings column */}
                    <Table.Td />
                  </Table.Tr>
                ))}
                {cohorts.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={visibleColumnCount + 1}>
                      <Text ta="center" c="dimmed" py="xl">
                        No cohorts found matching your filters
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalCount > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </Card>
      ) : (
        /* Grid View */
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {cohorts.map(cohort => {
              const completed = cohort.completed_sessions ?? 0;
              const total = cohort.session_count ?? 0;
              const progress = total > 0 ? (completed / total) * 100 : 0;

              return (
                <Card
                  key={cohort.id}
                  withBorder
                  p="md"
                  className={classes.cohortCard}
                  onClick={() => handleRowClick(cohort.id)}
                >
                  {/* Top Row: Code and Status */}
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" c="dimmed" fw={600}>
                      {cohort.code || '—'}
                    </Text>
                    <Badge
                      color={COHORT_STATUS_CONFIG[cohort.status].color}
                      variant={COHORT_STATUS_CONFIG[cohort.status].variant || 'light'}
                      size="sm"
                    >
                      {COHORT_STATUS_CONFIG[cohort.status].label}
                    </Badge>
                  </Group>

                  {/* Name */}
                  <Group gap="xs" wrap="nowrap" mb={4}>
                    <Text fw={600} size="md">
                      {cohort.name}
                    </Text>
                    {cohort.disabled_email_recipients?.length > 0 && (
                      <Tooltip label={`Emails disabled for: ${cohort.disabled_email_recipients.map((r: string) => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}`}>
                        <IconMailOff size={14} color="var(--mantine-color-gray-6)" />
                      </Tooltip>
                    )}
                  </Group>

                  {/* Partner */}
                  <Text size="sm" c="dimmed" mb="md">
                    {cohort.partner_name}
                  </Text>

                  {/* Bottom Row: Sessions Progress and Health */}
                  <Group justify="space-between" align="center">
                    <Group gap={8} wrap="nowrap">
                      <Text size="xs" c="dimmed">Sessions:</Text>
                      <Text size="sm" fw={500}>
                        {completed}/{total}
                      </Text>
                      <Progress
                        value={progress}
                        size="sm"
                        color="medleyGreen"
                        w={60}
                        className={classes.sessionProgress}
                      />
                    </Group>
                    <HealthIndicator healthStatus={cohort.health_status} status={cohort.status} />
                  </Group>
                </Card>
              );
            })}
          </SimpleGrid>

          {cohorts.length === 0 && (
            <Card withBorder p="xl">
              <Text ta="center" c="dimmed">
                No cohorts found matching your filters
              </Text>
            </Card>
          )}

          {totalCount > 0 && (
            <Card withBorder p={0} mt="md">
              <Pagination
                page={page}
                pageSize={pageSize}
                total={totalCount}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </Card>
          )}
        </>
      )}

      {/* Create Cohort Modal */}
      <CreateCohortModal
        opened={createModalOpened}
        onClose={closeCreateModal}
        onCreated={handleCohortCreated}
      />

      {/* Export Modal */}
      <ExportModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        entityType="cohorts"
        recordCount={totalCount}
        filterContext={{
          search: debouncedSearch || undefined,
          status: selectedStatusFilter === 'all' ? undefined : [selectedStatusFilter],
        }}
        currentViewColumns={columns.filter(c => c.visible).map(c => c.id)}
        filterSummary={[
          ...(debouncedSearch ? [{ label: 'Search', value: debouncedSearch }] : []),
          ...(selectedStatusFilter !== 'all' ? [{ label: 'Status', value: selectedStatusFilter }] : []),
        ]}
      />
    </Container>
  );
}

export default CohortsPage;
